// EventSource connection loop logic extracted from realtime-provider.tsx.
// Keeps the provider under 200 LOC while isolating reconnect/backoff state.
//
// Backoff schedule: 1s → 3s → 9s (×3 each attempt), cap at 9s.
// After MAX_RECONNECTS consecutive failures the loop calls onPollingFallback()
// so the provider can switch to interval-based cache invalidation.
import type { QueryClient } from "@tanstack/react-query";
import { dispatchTable, defaultDispatch, allRealtimeQueryKeys } from "./dispatch-table";
import { refreshJWTAndReconnect } from "./jwt-refresh";
import type { Frame, FrameFamily } from "./types";

export const MAX_RECONNECTS = 3;
const BACKOFF_SCHEDULE_MS = [1_000, 3_000, 9_000];
// After polling fallback engages, retry SSE every POLLING_RETRY_MS so a
// transient outage doesn't pin the tab to polling forever.
const POLLING_RETRY_MS = 60_000;

function backoffMs(attempt: number): number {
  return BACKOFF_SCHEDULE_MS[Math.min(attempt, BACKOFF_SCHEDULE_MS.length - 1)];
}

export interface ConnectionLoopOptions {
  orgId: string;
  families: FrameFamily[];
  qc: QueryClient;
  getLastEventId: () => number;
  setLastEventId: (id: number) => void;
  onOpen: (reconnectCount: number) => void;
  onPollingFallback: () => void;
  onStatusChange: (status: "connecting" | "open") => void;
}

export interface ConnectionHandle {
  /** Permanently dispose this loop, its EventSource, and pending reconnect timers. */
  close: () => void;
  /** Trigger a manual reconnect and reset backoff. No-op after close(). */
  forceReconnect: () => void;
}

/**
 * Opens an EventSource to the multiplexed realtime endpoint, dispatches
 * frames through the dispatch table, and manages reconnect backoff.
 *
 * Returns a handle with close() and forceReconnect() for the caller.
 */
export function startConnectionLoop(opts: ConnectionLoopOptions): ConnectionHandle {
  let disposed = false;
  let reconnectCount = 0;
  let currentSource: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function isCurrent(source: EventSource): boolean {
    return !disposed && source === currentSource;
  }

  function clearReconnectTimer() {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function resetConnection() {
    clearReconnectTimer();

    const source = currentSource;
    currentSource = null;
    source?.close();
  }

  function scheduleReconnect(delay: number, resetReconnectCount = false) {
    const scheduledTimer = setTimeout(() => {
      if (reconnectTimer !== scheduledTimer) return;

      reconnectTimer = null;
      if (resetReconnectCount) reconnectCount = 0;
      open();
    }, delay);
    reconnectTimer = scheduledTimer;
  }

  function reopenCurrentSource(source: EventSource, resetReconnectCount = false) {
    if (!isCurrent(source)) return;

    resetConnection();
    if (resetReconnectCount) reconnectCount = 0;
    open();
  }

  function buildUrl(): string {
    const lastId = opts.getLastEventId();
    const params = new URLSearchParams({
      org_id: opts.orgId,
      families: opts.families.join(","),
    });
    if (lastId > 0) params.set("last_event_id", String(lastId));
    return `/api/hub/realtime/events?${params.toString()}`;
  }

  function open() {
    if (disposed || currentSource !== null) return;

    opts.onStatusChange("connecting");
    const source = new EventSource(buildUrl(), { withCredentials: true });
    currentSource = source;

    source.onopen = () => {
      if (!isCurrent(source)) {
        source.close();
        return;
      }
      opts.onStatusChange("open");
      opts.onOpen(reconnectCount);
      reconnectCount = 0;
    };

    // Wire a listener for every known event name from the dispatch table,
    // plus "auth.expiring" which needs special handling before dispatch.
    const knownEvents = Object.keys(dispatchTable);
    knownEvents.forEach((eventName) => {
      source.addEventListener(eventName, (e: MessageEvent) => {
        handleMessage(eventName, e, source);
      });
    });

    // auth.expiring: rotate auth cookies before reopening the EventSource.
    source.addEventListener("auth.expiring", () => {
      if (!isCurrent(source)) return;

      refreshJWTAndReconnect(() => {
        reopenCurrentSource(source, true);
      }).catch(() => {
        // refresh helper logs its own warning; just reconnect.
        reopenCurrentSource(source);
      });
    });

    source.onerror = () => {
      if (!isCurrent(source)) return;

      resetConnection();

      reconnectCount += 1;
      if (reconnectCount >= MAX_RECONNECTS) {
        opts.onPollingFallback();
        // Schedule SSE retry so a transient outage doesn't pin the tab to polling
        // forever. On successful reopen, onOpen() resets reconnectCount
        // and the provider clears polling.
        scheduleReconnect(POLLING_RETRY_MS, true);
        return;
      }

      const delay = backoffMs(reconnectCount - 1);
      scheduleReconnect(delay);
    };
  }

  function handleMessage(eventName: string, e: MessageEvent, source: EventSource) {
    if (!isCurrent(source)) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(e.data as string);
    } catch {
      return; // malformed — skip, next event carries canonical state
    }

    // Determine family from eventName prefix (e.g. "kg.run.progress" → "kg")
    const dotIdx = eventName.indexOf(".");
    const family = (dotIdx > -1 ? eventName.slice(0, dotIdx) : eventName) as FrameFamily;

    // wirePayload from BE has shape {id, family, event, run_id, data: <outbox>}.
    // Unwrap to expose the outbox payload at frame.data for handlers; fall back
    // to the parsed object itself for test fixtures that build frames flat.
    const payload =
      parsed !== null && typeof parsed === "object" && "data" in parsed
        ? (parsed as { data: unknown }).data
        : parsed;

    const frame: Frame = {
      id: e.lastEventId ? Number(e.lastEventId) : 0,
      family,
      event: dotIdx > -1 ? eventName.slice(dotIdx + 1) : eventName,
      data: payload,
    };

    if (frame.id > 0) opts.setLastEventId(frame.id);

    const handler = dispatchTable[eventName];
    if (handler) {
      handler(frame, opts.qc);
    } else {
      defaultDispatch(eventName, frame);
    }
  }

  function close() {
    if (disposed) return;

    disposed = true;
    resetConnection();
  }

  function forceReconnect() {
    if (disposed) return;

    resetConnection();
    reconnectCount = 0;
    open();
  }

  open();

  return { close, forceReconnect };
}

/**
 * Builds a QueryClient predicate that matches any query whose first key
 * segment appears in allRealtimeQueryKeys. Used for on-reopen invalidation
 * and polling-fallback sweeps.
 */
export function realtimeKeyPredicate(query: { queryKey: readonly unknown[] }): boolean {
  const root = query.queryKey[0];
  return typeof root === "string" && (allRealtimeQueryKeys as readonly string[]).includes(root);
}
